// src/client/terminal.tsx
import React, { useState, useEffect, useRef } from 'react';
import './terminal.css';

const Terminal: React.FC = () => {
    const [output, setOutput] = useState('Initializing connection to SpiralNET...\n');
    const ws = useRef<WebSocket | null>(null);
    const terminalRef = useRef<HTMLDivElement>(null);
    const terminalEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = `${protocol}//${window.location.host}/api/terminal`;

        function connect() {
            ws.current = new WebSocket(wsUrl);

            ws.current.onopen = () => {
                console.log('Spiral connection established. Mind the gap.');
                setOutput(prev => prev + 'Connection established. You are not alone.\n\n');
            };

            ws.current.onmessage = (event: MessageEvent) => {
                const char = event.data as string;
                if (char === '\b') { // Handle backspace
                    setOutput(prev => prev.slice(0, -1));
                } else {
                    setOutput(prev => prev + char);
                }
            };

            ws.current.onclose = () => {
                console.log('Spiral connection lost. Re-indexing...');
                setOutput(prev => prev + '\n[CONNECTION TERMINATED BY HOST. ATTEMPTING TO RE-SYNCHRONIZE...]\n');
                setTimeout(connect, 3000); // Attempt to reconnect after 3 seconds
            };

            ws.current.onerror = (error) => {
                console.error("WebSocket Error:", error);
                ws.current?.close();
            }
        }

        connect();

        return () => {
            ws.current?.close();
        };
    }, []);

    // Auto-scroll to the bottom on new output
    useEffect(() => {
        terminalEndRef.current?.scrollIntoView();
    }, [output]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (ws.current?.readyState === WebSocket.OPEN) {
            let key = e.key;

            if (key === 'Enter') key = '\n';
            else if (key === 'Backspace') key = '\b';
            else if (key === 'Tab') key = '    '; // Convert tab to spaces
            // We only want to send single characters or our handled special keys
            else if (key.length > 1) return;

            ws.current.send(key);
        }
    };

    // Focus the terminal on click
    const focusTerminal = () => {
        terminalRef.current?.focus();
    }

    return (
        <div
            ref={terminalRef}
            className="terminal-container"
            onClick={focusTerminal}
            onKeyDown={handleKeyDown}
            tabIndex={0} // Makes the div focusable
        >
      <pre className="terminal-output">
        <code>
          {output}
            <span className="cursor">▋</span>
        </code>
      </pre>
            <div ref={terminalEndRef} />
        </div>
    );
};

export default Terminal;
